import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

type Provider = 'apollo' | 'perplexity' | 'claude' | 'openai';

interface IntegrationCredential {
  id: string;
  provider: Provider;
  provider_display: string;
  status: 'active' | 'inactive' | 'error';
  usage_limits: Record<string, unknown>;
  last_tested_at: string | null;
  last_error: string;
  created_at: string;
  updated_at: string;
}

const providers: Provider[] = ['apollo', 'perplexity', 'claude', 'openai'];

interface IntegrationTableRow {
  provider: Provider;
  credential?: IntegrationCredential;
}

const Integrations: React.FC = () => {
  const [rows, setRows] = useState<IntegrationCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState<string | null>(null);
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [newKeys, setNewKeys] = useState<Record<string, string>>({});
  const [statuses, setStatuses] = useState<Record<string, 'active' | 'inactive' | 'error'>>({});
  const [testNotes, setTestNotes] = useState<Record<string, string>>({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/platform/admin/integrations/');
      const list = Array.isArray(data) ? data : data.results || [];
      setRows(list);
      const statusMap: Record<string, 'active' | 'inactive' | 'error'> = {};
      list.forEach((item: IntegrationCredential) => {
        statusMap[item.provider] = item.status;
      });
      setStatuses(statusMap);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upsertCredential = async (provider: Provider) => {
    const existing = rows.find((item) => item.provider === provider);
    const payload: Record<string, unknown> = {
      provider,
      status: statuses[provider] || 'active',
    };
    if (newKeys[provider]?.trim()) payload.api_key = newKeys[provider].trim();
    setSavingProvider(provider);
    try {
      if (existing) {
        await apiClient.patch(`/platform/admin/integrations/${provider}/`, payload);
      } else {
        await apiClient.post('/platform/admin/integrations/', payload);
      }
      setNewKeys((prev) => ({ ...prev, [provider]: '' }));
      await load();
    } finally {
      setSavingProvider(null);
    }
  };

  const testConnection = async (provider: Provider) => {
    setTestingProvider(provider);
    try {
      const result = await apiClient.post(`/platform/admin/integrations/${provider}/test/`, {}) as {
        companies_ok?: boolean;
        people_ok?: boolean;
        people_search_enabled?: boolean;
      };
      if (provider === 'apollo' && (result.companies_ok !== undefined || result.people_ok !== undefined)) {
        const companies = result.companies_ok ? 'ok' : 'failed';
        const people = result.people_ok ? 'ok' : 'failed';
        const enabled = result.people_search_enabled ? 'enabled' : 'disabled';
        setTestNotes((prev) => ({
          ...prev,
          apollo: `Companies: ${companies} · People: ${people} · People search ${enabled}`,
        }));
      } else {
        setTestNotes((prev) => ({ ...prev, [provider]: 'Connection test completed.' }));
      }
      await load();
    } finally {
      setTestingProvider(null);
    }
  };

  const integrationRows = useMemo<IntegrationTableRow[]>(
    () => providers.map((provider) => ({ provider, credential: rows.find((item) => item.provider === provider) })),
    [rows]
  );

  const columns = useMemo<Column<IntegrationTableRow>[]>(
    () => [
      {
        id: 'provider',
        header: 'Provider',
        mobilePriority: 'primary',
        mobileLabel: 'Provider',
        render: (row) => <span className="text-white font-medium capitalize">{row.provider}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (row) => (
          <select
            value={statuses[row.provider] || row.credential?.status || 'active'}
            onChange={(e) =>
              setStatuses((prev) => ({
                ...prev,
                [row.provider]: e.target.value as 'active' | 'inactive' | 'error',
              }))
            }
            className="rounded border border-border bg-surface px-2 py-1 text-fg"
          >
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="error">error</option>
          </select>
        ),
      },
      {
        id: 'api_key',
        header: 'API Key',
        mobileLabel: 'API Key',
        render: (row) => (
          <input
            type="password"
            value={newKeys[row.provider] || ''}
            onChange={(e) => setNewKeys((prev) => ({ ...prev, [row.provider]: e.target.value }))}
            placeholder={row.credential ? '•••••••• (leave blank to keep)' : 'Enter API key'}
            className="w-full rounded border border-border bg-surface px-2 py-1 text-fg"
          />
        ),
      },
      {
        id: 'last_test',
        header: 'Last Test',
        mobileLabel: 'Last Test',
        render: (row) => (
          <div className="text-fg-muted">
            <div>
              {row.credential?.last_tested_at
                ? new Date(row.credential.last_tested_at).toLocaleString()
                : 'Never'}
            </div>
            {testNotes[row.provider] && (
              <div className="text-xs text-fg-secondary mt-1">{testNotes[row.provider]}</div>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        className: 'text-right',
        render: (row) => (
          <div data-row-action className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => upsertCredential(row.provider)}
              disabled={savingProvider === row.provider}
              className="px-3 py-1 rounded border border-border text-fg hover:bg-surface-muted"
            >
              {savingProvider === row.provider ? 'Saving...' : row.credential ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => testConnection(row.provider)}
              disabled={!row.credential || testingProvider === row.provider}
              className="px-3 py-1 rounded bg-primary text-primary-fg disabled:opacity-60"
            >
              {testingProvider === row.provider ? 'Testing...' : 'Test'}
            </button>
          </div>
        ),
      },
    ],
    [newKeys, rows, savingProvider, statuses, testNotes, testingProvider]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Platform Integrations</h1>
        <button
          type="button"
          onClick={load}
          className="px-4 py-2 rounded-lg border border-border hover:border-amber-500/50 text-fg-secondary text-sm"
        >
          Refresh
        </button>
      </div>

      <DataListTable<IntegrationTableRow>
        columns={columns}
        rows={integrationRows}
        getRowKey={(row) => row.provider}
        loading={loading}
        emptyContent={
          <div className="p-4 text-fg-muted">No integrations configured.</div>
        }
      />
    </div>
  );
};

export default Integrations;
